import React, { useMemo, useState } from 'react';
import { useApp } from '../context/AppContext';
import { formatCurrency, formatDateOnlyDisplay, parseDateOnlyLocal } from '../utils/helpers';

const SalesReport = ({ onOpenVehicleDetail }) => {
  const { vehicles } = useApp();
  const [activeFilter, setActiveFilter] = useState(null);
  const [daysFilter, setDaysFilter] = useState(null);
  const [showCostColumns, setShowCostColumns] = useState(false);
  
  // Helper function to calculate days in stock
  const calculateDaysInStock = (vehicle) => {
    const stockDate = vehicle.dropOffDate || vehicle.receivedDate;
    if (!stockDate) return null;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const stock = parseDateOnlyLocal(stockDate);
    if (!stock) return null;
    const diffTime = today - stock;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays >= 0 ? diffDays : 0;
  };
  
  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return formatDateOnlyDisplay(dateString, {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }) || 'N/A';
  };

  const salesData = useMemo(() => {
    // Get all sales vehicles and include those with deposit taken and sold status
    const salesVehicles = vehicles.filter(veh => veh.status === 'Sales' && !veh.deletedAt);
    
    const stats = {
      totalVehicles: salesVehicles.length,
      salesMade: 0,
      depositsTaken: 0,
      revenueDue: 0,
      totalCost: 0,
      totalPrepCost: 0,
      totalWarranty: 0,
      totalMisc: 0,
      totalSalePrice: 0,
      totalProfit: 0,
      vehicles: []
    };

    salesVehicles.forEach((veh) => {
      // Find the original index in the full vehicles array
      const originalIndex = vehicles.findIndex(v => v.reg === veh.reg);
      const cost = veh.cost || 0;
      const prepCost = veh.prepCost || 0;
      const warranty = veh.warranty || 0;
      const misc = veh.misc || 0;
      const salePrice = veh.salePrice || 0;
      const totalCost = cost + prepCost + warranty + misc;
      const profit = salePrice - totalCost;

      // Check both dealStage and salesStage for compatibility
      // salesStage values: 'prep', 'forSale', 'deposit', 'sold'
      // dealStage values: 'inStock', 'inPrep', 'forSale', 'depositTaken', 'sold', 'collected'
      let dealStage = veh.dealStage;
      
      // If no dealStage, check salesStage and map it
      if (!dealStage && veh.salesStage) {
        if (veh.salesStage === 'deposit') dealStage = 'depositTaken';
        else if (veh.salesStage === 'prep') dealStage = 'inPrep';
        else if (veh.salesStage === 'forSale') dealStage = 'forSale';
        else if (veh.salesStage === 'sold') dealStage = 'sold';
      }
      
      // Default to inStock if neither field exists
      if (!dealStage) dealStage = 'inStock';
      
      const isSold = dealStage === 'sold' || dealStage === 'collected' || veh.salesStage === 'sold';
      const hasDeposit = dealStage === 'depositTaken' || veh.salesStage === 'deposit' || isSold;

      if (isSold) {
        stats.salesMade++;
      }
      if (hasDeposit) {
        stats.depositsTaken++;
      }
      if (!isSold && salePrice > 0) {
        stats.revenueDue += salePrice;
      }

      stats.totalCost += cost;
      stats.totalPrepCost += prepCost;
      stats.totalWarranty += warranty;
      stats.totalMisc += misc;
      stats.totalSalePrice += salePrice;
      stats.totalProfit += profit;

      // Calculate days in stock
      const daysInStock = calculateDaysInStock(veh);
      const stockDate = veh.dropOffDate || veh.receivedDate;
      
      stats.vehicles.push({
        reg: veh.reg,
        vehicle: veh.vehicle,
        dealStage: dealStage,
        salesStage: veh.salesStage, // Store original salesStage for reference
        cost: cost,
        prepCost: prepCost,
        warranty: warranty,
        misc: misc,
        totalCost: totalCost,
        salePrice: salePrice,
        profit: profit,
        contact: veh.contact || 'N/A',
        originalIndex: originalIndex !== -1 ? originalIndex : null, // Store the original index in the vehicles array
        // Add date fields for sorting
        dropOffDate: veh.dropOffDate || veh.receivedDate,
        receivedDate: veh.receivedDate || veh.dropOffDate,
        collectionDate: veh.collectionDate,
        // Add days in stock info
        daysInStock: daysInStock,
        stockDate: stockDate
      });
    });

    // Sort vehicles by most recent date first (dropOffDate or receivedDate, then collectionDate for sold vehicles)
    stats.vehicles.sort((a, b) => {
      // For sold/collected vehicles, prefer collectionDate
      const dateA = a.dealStage === 'sold' || a.dealStage === 'collected' 
        ? (a.collectionDate || a.dropOffDate || a.receivedDate)
        : (a.dropOffDate || a.receivedDate);
      const dateB = b.dealStage === 'sold' || b.dealStage === 'collected'
        ? (b.collectionDate || b.dropOffDate || b.receivedDate)
        : (b.dropOffDate || b.receivedDate);
      
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1; // Put vehicles without dates at the end
      if (!dateB) return -1;
      
      const timeA = new Date(dateA).getTime();
      const timeB = new Date(dateB).getTime();
      
      return timeB - timeA; // Descending order (most recent first)
    });

    return stats;
  }, [vehicles]);

  const filteredVehicles = useMemo(() => {
    let filtered = salesData.vehicles;
    
    // Apply active filter
    if (activeFilter) {
      switch (activeFilter) {
        case 'totalVehicles':
          filtered = salesData.vehicles;
          break;
        case 'salesMade':
          filtered = salesData.vehicles.filter(v => 
            v.dealStage === 'sold' || 
            v.dealStage === 'collected'
          );
          break;
        case 'depositsTaken':
          filtered = salesData.vehicles.filter(v => 
            v.dealStage === 'depositTaken' || 
            v.dealStage === 'sold' || 
            v.dealStage === 'collected'
          );
          break;
        case 'revenueDue':
          filtered = salesData.vehicles.filter(v => {
            const isSold = v.dealStage === 'sold' || v.dealStage === 'collected';
            return !isSold && v.salePrice > 0;
          });
          break;
        default:
          filtered = salesData.vehicles;
      }
    }
    
    // Apply days filter
    if (daysFilter !== null) {
      filtered = filtered.filter(v => {
        if (v.daysInStock === null) return false;
        return v.daysInStock >= daysFilter;
      });
    }
    
    return filtered;
  }, [salesData.vehicles, activeFilter, daysFilter]);

  const handleCardClick = (filterType) => {
    if (activeFilter === filterType) {
      setActiveFilter(null); // Toggle off if already active
    } else {
      setActiveFilter(filterType);
    }
  };

  const getStageLabel = (stage, salesStage = null) => {
    // If salesStage is provided and stage is not recognized, use salesStage
    if (salesStage && !['inStock', 'inPrep', 'forSale', 'depositTaken', 'sold', 'collected'].includes(stage)) {
      stage = salesStage;
    }
    
    const labels = {
      'inStock': 'Vehicle in Stock',
      'inPrep': 'In Prep',
      'prep': 'Vehicle in Prep',
      'forSale': 'For Sale',
      'depositTaken': 'Deposit Taken',
      'deposit': 'Deposit Taken',
      'sold': 'Sold',
      'collected': 'Collected'
    };
    return labels[stage] || stage;
  };

  const getStageColor = (stage, salesStage = null) => {
    // If salesStage is provided and stage is not recognized, use salesStage
    if (salesStage && !['inStock', 'inPrep', 'forSale', 'depositTaken', 'sold', 'collected'].includes(stage)) {
      stage = salesStage;
    }
    
    // Only use colors for deposit taken and sold vehicles
    const isDeposit = stage === 'depositTaken' || stage === 'deposit' || salesStage === 'deposit';
    const isSold = stage === 'sold' || stage === 'collected' || salesStage === 'sold';
    
    if (isDeposit) {
      return { bg: '#3B82F6', color: '#FFFFFF' }; // Blue
    }
    if (isSold) {
      return { bg: '#10B981', color: '#FFFFFF' }; // Green
    }
    
    // All other stages use neutral styling
    return { bg: 'transparent', color: 'var(--text-dark)', border: '1px solid var(--border)' };
  };

  return (
    <div className="vehicle-inner sales-report-page">
      <div className="vehicle-header">
        <div>
          <h2>Sales Report</h2>
          <p className="vehicle-subtitle">Complete breakdown of sales vehicles and financials</p>
        </div>
      </div>

      <div className="vehicle-content">
        {/* Summary Cards */}
        <div className="financial-overview" style={{ marginBottom: '32px' }}>
          <div 
            className={`financial-card financial-billed ${activeFilter === 'totalVehicles' ? 'card-filter-active' : ''}`}
            onClick={() => handleCardClick('totalVehicles')}
            style={{ cursor: 'pointer' }}
          >
            <div className="financial-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 11l3 3L22 4"></path>
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
              </svg>
            </div>
            <div className="financial-content">
              <div className="financial-label">Total Vehicles</div>
              <div className="financial-value">{salesData.totalVehicles}</div>
              {activeFilter === 'totalVehicles' && (
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                  Showing {filteredVehicles.length} vehicle{filteredVehicles.length !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          </div>

          <div 
            className={`financial-card financial-received ${activeFilter === 'salesMade' ? 'card-filter-active' : ''}`}
            onClick={() => handleCardClick('salesMade')}
            style={{ cursor: 'pointer' }}
          >
            <div className="financial-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            </div>
            <div className="financial-content">
              <div className="financial-label">Sales Made</div>
              <div className="financial-value">{salesData.salesMade}</div>
              {activeFilter === 'salesMade' && (
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                  Showing {filteredVehicles.length} vehicle{filteredVehicles.length !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          </div>

          <div 
            className={`financial-card financial-outstanding ${activeFilter === 'depositsTaken' ? 'card-filter-active' : ''}`}
            onClick={() => handleCardClick('depositsTaken')}
            style={{ cursor: 'pointer' }}
          >
            <div className="financial-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            </div>
            <div className="financial-content">
              <div className="financial-label">Deposits Taken</div>
              <div className="financial-value">{salesData.depositsTaken}</div>
              {activeFilter === 'depositsTaken' && (
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                  Showing {filteredVehicles.length} vehicle{filteredVehicles.length !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          </div>

          <div 
            className={`financial-card ${activeFilter === 'revenueDue' ? 'card-filter-active' : ''}`}
            onClick={() => handleCardClick('revenueDue')}
            style={{ 
              borderTop: '4px solid #3B82F6',
              cursor: 'pointer'
            }}
          >
            <div className="financial-icon" style={{ background: 'rgba(59, 130, 246, 0.12)', color: '#3B82F6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', fontWeight: 'bold' }}>
              £
            </div>
            <div className="financial-content">
              <div className="financial-label">Revenue Due</div>
              <div className="financial-value" style={{ color: '#3B82F6' }}>
                {formatCurrency(salesData.revenueDue)}
              </div>
              {activeFilter === 'revenueDue' && (
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                  Showing {filteredVehicles.length} vehicle{filteredVehicles.length !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Financial Breakdown */}
        <div className="sales-report-breakdown" style={{ 
          background: 'var(--card-bg)', 
          borderRadius: '16px', 
          padding: '24px', 
          border: '1px solid var(--border)',
          marginBottom: '32px'
        }}>
          <h3 style={{ 
            fontSize: '20px', 
            fontWeight: '600', 
            marginBottom: '20px',
            color: 'var(--text-dark)'
          }}>
            Financial Breakdown
          </h3>
          
          <div style={{ display: 'grid', gap: '12px' }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              padding: '12px 0',
              borderBottom: '1px solid var(--border)'
            }}>
              <span style={{ color: 'var(--text-muted)', fontWeight: '500' }}>Total Cost</span>
              <span style={{ fontWeight: '600', color: 'var(--text-dark)' }}>
                {formatCurrency(salesData.totalCost)}
              </span>
            </div>
            
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              padding: '12px 0',
              borderBottom: '1px solid var(--border)'
            }}>
              <span style={{ color: 'var(--text-muted)', fontWeight: '500' }}>Total Prep Cost</span>
              <span style={{ fontWeight: '600', color: 'var(--text-dark)' }}>
                {formatCurrency(salesData.totalPrepCost)}
              </span>
            </div>
            
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              padding: '12px 0',
              borderBottom: '1px solid var(--border)'
            }}>
              <span style={{ color: 'var(--text-muted)', fontWeight: '500' }}>Total Warranty</span>
              <span style={{ fontWeight: '600', color: 'var(--text-dark)' }}>
                {formatCurrency(salesData.totalWarranty)}
              </span>
            </div>
            
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              padding: '12px 0',
              borderBottom: '1px solid var(--border)'
            }}>
              <span style={{ color: 'var(--text-muted)', fontWeight: '500' }}>Total Misc</span>
              <span style={{ fontWeight: '600', color: 'var(--text-dark)' }}>
                {formatCurrency(salesData.totalMisc)}
              </span>
            </div>
            
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              padding: '16px 0',
              borderTop: '2px solid var(--border)',
              borderBottom: '2px solid var(--border)',
              marginTop: '8px'
            }}>
              <span style={{ fontWeight: '700', fontSize: '16px', color: 'var(--text-dark)' }}>
                Total Costs
              </span>
              <span style={{ fontWeight: '700', fontSize: '16px', color: 'var(--text-dark)' }}>
                {formatCurrency(salesData.totalCost + salesData.totalPrepCost + salesData.totalWarranty + salesData.totalMisc)}
              </span>
            </div>
            
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              padding: '12px 0',
              borderBottom: '1px solid var(--border)'
            }}>
              <span style={{ color: 'var(--text-muted)', fontWeight: '500' }}>Total Sale Price</span>
              <span style={{ fontWeight: '600', color: 'var(--text-dark)' }}>
                {formatCurrency(salesData.totalSalePrice)}
              </span>
            </div>
            
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              borderTop: '2px solid var(--border)',
              background: 'var(--light-bg)',
              borderRadius: '8px',
              padding: '16px',
              marginTop: '12px'
            }}>
              <span style={{ fontWeight: '700', fontSize: '18px', color: 'var(--text-dark)' }}>
                Total Profit
              </span>
              <span style={{ 
                fontWeight: '700', 
                fontSize: '18px',
                color: salesData.totalProfit >= 0 ? '#10B981' : '#EF4444'
              }}>
                {formatCurrency(salesData.totalProfit)}
              </span>
            </div>
          </div>
        </div>

        {/* Vehicle Breakdown Table */}
        <div className="sales-report-vehicle-table" style={{ 
          background: 'var(--card-bg)', 
          borderRadius: '16px', 
          padding: '24px', 
          border: '1px solid var(--border)'
        }}>
          <div className="sales-report-vehicle-table-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
            <h3 style={{ 
              fontSize: '20px', 
              fontWeight: '600',
              color: 'var(--text-dark)',
              margin: 0
            }}>
              Vehicle Breakdown
              {(activeFilter || daysFilter !== null) && (
                <span style={{ 
                  fontSize: '14px', 
                  fontWeight: '400', 
                  color: 'var(--text-muted)',
                  marginLeft: '12px'
                }}>
                  ({filteredVehicles.length} of {salesData.vehicles.length})
                </span>
              )}
            </h3>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                fontSize: '13px',
                color: 'var(--text-dark)',
                fontWeight: '500'
              }}>
                <span>Days in Stock:</span>
                <select
                  value={daysFilter !== null ? daysFilter : ''}
                  onChange={(e) => setDaysFilter(e.target.value === '' ? null : parseInt(e.target.value))}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '8px',
                    border: '1px solid var(--border)',
                    background: 'var(--card-bg)',
                    color: 'var(--text-dark)',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    minWidth: '120px'
                  }}
                >
                  <option value="">All</option>
                  <option value="30">30+ days</option>
                  <option value="60">60+ days</option>
                  <option value="90">90+ days</option>
                  <option value="120">120+ days</option>
                </select>
              </label>
              <button
                onClick={() => setShowCostColumns(s => !s)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: '1px solid var(--border)',
                  background: showCostColumns ? 'rgba(79, 141, 243, 0.15)' : 'var(--light-bg)',
                  color: 'var(--text-dark)',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(79, 141, 243, 0.1)';
                  e.currentTarget.style.borderColor = 'rgba(79, 141, 243, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = showCostColumns ? 'rgba(79, 141, 243, 0.15)' : 'var(--light-bg)';
                  e.currentTarget.style.borderColor = 'var(--border)';
                }}
              >
                {showCostColumns ? 'Hide cost columns' : 'Show cost columns'}
              </button>
              {(activeFilter || daysFilter !== null) && (
                <button
                  onClick={() => {
                    setActiveFilter(null);
                    setDaysFilter(null);
                  }}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: '1px solid var(--border)',
                    background: 'var(--light-bg)',
                    color: 'var(--text-dark)',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(79, 141, 243, 0.1)';
                    e.currentTarget.style.borderColor = 'rgba(79, 141, 243, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'var(--light-bg)';
                    e.currentTarget.style.borderColor = 'var(--border)';
                  }}
                >
                  Clear All Filters
                </button>
              )}
            </div>
          </div>
          
          {salesData.vehicles.length === 0 ? (
            <div className="empty-state">No sales vehicles found.</div>
          ) : filteredVehicles.length === 0 ? (
            <div className="empty-state">No vehicles match the selected filter.</div>
          ) : (
            <div className="sales-report-table-wrap" style={{ overflowX: 'auto' }}>
              <table className="sales-report-table" style={{ 
                width: '100%', 
                minWidth: showCostColumns ? '1100px' : '700px',
                borderCollapse: 'collapse',
                tableLayout: 'fixed',
                fontSize: '14px'
              }}>
                <colgroup>
                  {showCostColumns ? (
                    <>
                      <col style={{ width: '9%' }} />
                      <col style={{ width: '21%' }} />
                      <col style={{ width: '12%' }} />
                      <col style={{ width: '8%' }} />
                      <col style={{ width: '6%' }} />
                      <col style={{ width: '6%' }} />
                      <col style={{ width: '5%' }} />
                      <col style={{ width: '6%' }} />
                      <col style={{ width: '5%' }} />
                      <col style={{ width: '8%' }} />
                      <col style={{ width: '6%' }} />
                      <col style={{ width: '8%' }} />
                    </>
                  ) : (
                    <>
                      <col style={{ width: '10%' }} />
                      <col style={{ width: '28%' }} />
                      <col style={{ width: '14%' }} />
                      <col style={{ width: '10%' }} />
                      <col style={{ width: '10%' }} />
                      <col style={{ width: '14%' }} />
                      <col style={{ width: '14%' }} />
                    </>
                  )}
                </colgroup>
                <thead>
                  <tr style={{ 
                    borderBottom: '2px solid var(--border)',
                    textAlign: 'left'
                  }}>
                    <th style={{ padding: '12px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.1em', whiteSpace: 'nowrap' }}>Reg</th>
                    <th style={{ padding: '12px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.1em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Vehicle</th>
                    <th style={{ padding: '12px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.1em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Stage</th>
                    <th style={{ padding: '12px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.1em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>In Stock Date</th>
                    <th style={{ padding: '12px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.1em', textAlign: 'center', whiteSpace: 'nowrap' }}>Days</th>
                    {showCostColumns && (
                      <>
                        <th style={{ padding: '12px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.1em', textAlign: 'right', whiteSpace: 'nowrap' }}>Cost</th>
                        <th style={{ padding: '12px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.1em', textAlign: 'right', whiteSpace: 'nowrap' }}>Prep</th>
                        <th style={{ padding: '12px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.1em', textAlign: 'right', whiteSpace: 'nowrap' }}>Warranty</th>
                        <th style={{ padding: '12px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.1em', textAlign: 'right', whiteSpace: 'nowrap' }}>Misc</th>
                        <th style={{ padding: '12px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.1em', textAlign: 'right', whiteSpace: 'nowrap' }}>Total Cost</th>
                      </>
                    )}
                    <th style={{ padding: '12px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.1em', textAlign: 'right', whiteSpace: 'nowrap' }}>Profit</th>
                    <th style={{ padding: '12px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.1em', textAlign: 'right', whiteSpace: 'nowrap' }}>Sale Price</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredVehicles.map((veh, idx) => (
                    <tr 
                      key={idx}
                      onClick={() => {
                        if (onOpenVehicleDetail && veh.originalIndex !== null) {
                          onOpenVehicleDetail(veh.originalIndex);
                        }
                      }}
                      style={{ 
                        borderBottom: '1px solid var(--border)',
                        transition: 'background 0.2s',
                        cursor: onOpenVehicleDetail && veh.originalIndex !== null ? 'pointer' : 'default'
                      }}
                      onMouseEnter={(e) => {
                        if (onOpenVehicleDetail && veh.originalIndex !== null) {
                          e.currentTarget.style.background = 'var(--light-bg)';
                        } else {
                          e.currentTarget.style.background = 'var(--light-bg)';
                        }
                      }}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <td data-label="Reg" style={{ padding: '12px', fontWeight: '600', color: 'var(--text-dark)', whiteSpace: 'nowrap' }}>{veh.reg}</td>
                      <td data-label="Vehicle" style={{ padding: '12px', color: 'var(--text-dark)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{veh.vehicle}</td>
                      <td data-label="Stage" style={{ padding: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {(() => {
                          const stageColor = getStageColor(veh.dealStage, veh.salesStage);
                          const label = getStageLabel(veh.dealStage, veh.salesStage);
                          return (
                            <span style={{
                              padding: '4px 10px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: '600',
                              background: stageColor.bg,
                              color: stageColor.color,
                              border: stageColor.border || 'none',
                              display: 'inline-block',
                              whiteSpace: 'nowrap',
                              lineHeight: '1.3',
                              maxWidth: '100%',
                              boxSizing: 'border-box'
                            }}>
                              {label}
                            </span>
                          );
                        })()}
                      </td>
                      <td data-label="In Stock Date" style={{ padding: '12px', color: 'var(--text-dark)', fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {formatDate(veh.stockDate)}
                      </td>
                      <td data-label="Days" style={{ 
                        padding: '12px', 
                        textAlign: 'center', 
                        color: veh.daysInStock !== null ? (veh.daysInStock >= 90 ? '#EF4444' : veh.daysInStock >= 60 ? '#F59E0B' : 'var(--text-dark)') : 'var(--text-muted)',
                        fontWeight: veh.daysInStock !== null ? '600' : '400'
                      }}>
                        {veh.daysInStock !== null ? (
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '8px',
                            background: veh.daysInStock >= 90 ? 'rgba(239, 68, 68, 0.1)' : veh.daysInStock >= 60 ? 'rgba(245, 158, 11, 0.1)' : 'rgba(79, 141, 243, 0.1)',
                            display: 'inline-block'
                          }}>
                            {veh.daysInStock} {veh.daysInStock === 1 ? 'day' : 'days'}
                          </span>
                        ) : (
                          'N/A'
                        )}
                      </td>
                      {showCostColumns && (
                        <>
                          <td data-label="Cost" style={{ padding: '12px', textAlign: 'right', color: 'var(--text-dark)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{formatCurrency(veh.cost)}</td>
                          <td data-label="Prep" style={{ padding: '12px', textAlign: 'right', color: 'var(--text-dark)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{formatCurrency(veh.prepCost)}</td>
                          <td data-label="Warranty" style={{ padding: '12px', textAlign: 'right', color: 'var(--text-dark)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{formatCurrency(veh.warranty)}</td>
                          <td data-label="Misc" style={{ padding: '12px', textAlign: 'right', color: 'var(--text-dark)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{formatCurrency(veh.misc)}</td>
                          <td data-label="Total Cost" style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: 'var(--text-dark)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{formatCurrency(veh.totalCost)}</td>
                        </>
                      )}
                      <td data-label="Profit" style={{
                        padding: '12px',
                        textAlign: 'right',
                        fontWeight: '700',
                        color: veh.profit >= 0 ? '#10B981' : '#EF4444',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {formatCurrency(veh.profit)}
                      </td>
                      <td data-label="Sale Price" style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: 'var(--text-dark)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{formatCurrency(veh.salePrice)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SalesReport;

